import { useLexicalComposerContext } from '@lexical/react/LexicalComposerContext';
import type { FCWithChildren } from '@pbk-web/types';
import type { RefObject } from 'react';
import { createContext, useCallback, useContext, useEffect, useMemo, useRef, useState } from 'react';

type Context = {
  /** True while the caret is in the editor, a toolbar control is being used, or a toolbar menu is open. */
  focused: boolean;
  /** The element the whole editor (chrome + content) is rendered into. */
  wrapperRef: RefObject<HTMLDivElement | null>;
  /** Called by the wrapper's `onFocus`; focus is given up by the listeners below, not by a handler. */
  onFocusIn: () => void;
  /**
   * Toolbar menus render in a portal, so a click in one looks like a click outside the wrapper.
   * Each open menu registers itself here to hold the editor focused for as long as it is open.
   */
  retainFocus: (id: string, retain: boolean) => void;
  /**
   * Deactivates the editor and gives up DOM focus (the "submit" of Enter / Ctrl+Enter).
   * - Needed because `editor.blur()` alone sends a `focusout` without a `relatedTarget`, which the
   *   listeners below deliberately ignore (that's what a tab switch looks like).
   */
  requestBlur: () => void;
};

const EditorChromeContext = createContext<Context>(null!);

export const useEditorChrome = () => {
  const context = useContext(EditorChromeContext);

  if (!context) {
    throw new Error('useEditorChrome must be used within an EditorChromeProvider');
  }

  return context;
};

/**
 * Tracks whether the editor as a whole is "active", the way Froala's `data-froala-focused` does:
 * the toolbar is only shown while active and the border switches to the accent colour. Focus is
 * held across clicks on the toolbar and inside portalled menus, and released by a click outside
 * or by focus moving to an unrelated element.
 */
export const EditorChromeProvider: FCWithChildren = ({ children }) => {
  const [editor] = useLexicalComposerContext();
  const wrapperRef = useRef<HTMLDivElement>(null);
  const [focused, setFocused] = useState(false);
  const retainedRef = useRef(new Set<string>());

  const onFocusIn = useCallback(() => setFocused(true), []);

  const retainFocus = useCallback((id: string, retain: boolean) => {
    if (retain) {
      retainedRef.current.add(id);
      setFocused(true);
    } else {
      retainedRef.current.delete(id);
    }
  }, []);

  const requestBlur = useCallback(() => {
    setFocused(false);
    editor.blur();
  }, [editor]);

  // Attached once on mount, not gated on `focused`: focus can leave the editor in the same tick
  // it arrives (another field stealing it during hydration), and a listener mounted by that
  // render would miss the `focusout` and leave the chrome stuck open. Both handlers read
  // `wrapperRef` when they fire, so the wrapper is always there by then, and releasing focus the
  // editor never had is a no-op.
  useEffect(() => {
    // A portalled menu (or the popover root the slash menu uses) is logically part of the editor.
    const isInsideEditor = (node: Node | null) => {
      if (!node) return false;
      if (wrapperRef.current?.contains(node)) return true;
      return node instanceof Element ? !!node.closest('[data-lexical-portal],#popover-root') : false;
    };

    const release = (next: Node | null) => {
      if (isInsideEditor(next)) return;
      if (retainedRef.current.size > 0) return;
      setFocused(false);
    };

    const onMouseDown = (e: MouseEvent) => release(e.target as Node);

    const onFocusOut = (e: FocusEvent) => {
      // `relatedTarget` is null when focus leaves the document entirely (tab switch) — keep the
      // chrome as it is, the way Froala does, rather than collapsing the toolbar under the user.
      if (!e.relatedTarget) return;
      release(e.relatedTarget as Node);
    };

    document.addEventListener('mousedown', onMouseDown);
    document.addEventListener('focusout', onFocusOut, true);

    return () => {
      document.removeEventListener('mousedown', onMouseDown);
      document.removeEventListener('focusout', onFocusOut, true);
    };
  }, []);

  const value = useMemo(() => ({ focused, wrapperRef, onFocusIn, retainFocus, requestBlur }), [focused, onFocusIn, retainFocus, requestBlur]);

  return <EditorChromeContext.Provider value={value}>{children}</EditorChromeContext.Provider>;
};
